import { useEffect, useMemo, useRef } from 'react';
import { TFunction } from 'types/i18n';
import { isPlainObject } from './lib/translationGuards';

export interface UseTranslatedArrayOptions {
  /**
   * Optional sub-property to read from the translation at `key`. Useful when
   * the array lives inside an object (e.g. `{ items: [...] }`); the hook
   * descends into `subPath` and validates that the value there is an array.
   */
  subPath?: string;
  /**
   * Called when the resolved translation is not an array. Defaults to logging
   * a console.error. Pass a no-op (`() => {}`) to suppress logging — the hook
   * captures the latest `onInvalid` in a ref, so callback identity does not
   * affect when the side effect fires (it runs only when the resolved value
   * actually transitions to an invalid shape).
   */
  onInvalid?: (key: string, actualType: string) => void;
}

const defaultOnInvalid = (key: string, actualType: string): void => {
  // eslint-disable-next-line no-console
  console.error(
    `[useTranslatedArray] Expected an array at "${key}", got ${actualType}. Falling back to [].`,
  );
};

const resolveActualType = (value: unknown): string => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

interface ArrayShape<T> {
  value: T[];
  isValid: boolean;
  actualType: string;
}

/**
 * Resolve the array at `t(key, { returnObjects: true })`, optionally descending
 * into `subPath`. Localizes the only required `as T[]` cast in this hook: after
 * `Array.isArray(resolved)` TypeScript narrows the value to `any[]`, but it
 * cannot prove the element type — `T` is the caller's contract with the
 * translation file, validated at runtime by the consumer (e.g.
 * `isValidFaqQuestion`) when shape matters.
 */
const resolveArrayShape = <T>(
  root: unknown,
  subPath: string | undefined,
): ArrayShape<T> => {
  let resolved: unknown = root;
  if (subPath) {
    resolved = isPlainObject(root) ? root[subPath] : undefined;
  }
  if (Array.isArray(resolved)) {
    return { value: resolved as T[], isValid: true, actualType: 'array' };
  }
  return {
    value: [],
    isValid: false,
    actualType: resolveActualType(resolved),
  };
};

/**
 * Read a translation value as an array, with a defensive fallback to `[]`
 * when the translation file accidentally ships the value as a string,
 * object, or undefined.
 *
 * Centralizes the `Array.isArray` guard that previously lived at every call
 * site (see ESCR-1618: a stringified array in non-English translation files
 * killed the SSN verification welcome page for non-English candidates).
 */
const useTranslatedArray = <T = unknown>(
  key: string,
  t: TFunction,
  options: UseTranslatedArrayOptions = {},
): T[] => {
  const { onInvalid = defaultOnInvalid, subPath } = options;

  // Mirror the latest `onInvalid` into a ref so the side-effect below does
  // NOT depend on its identity. Inline lambdas (a new function reference
  // every render) would otherwise cause the effect to re-run every render
  // even when the validity state had not changed. We update the ref
  // synchronously during render — the idiomatic pattern for keeping a ref
  // in sync with a value that is only read inside an effect, and avoids
  // any subtle ordering dependency between two effects.
  const onInvalidRef = useRef(onInvalid);
  onInvalidRef.current = onInvalid;

  const fullKey = useMemo(
    () => (subPath ? `${key}.${subPath}` : key),
    [key, subPath],
  );

  const { value, isValid, actualType } = useMemo<ArrayShape<T>>(() => {
    if (!key) return { value: [], isValid: true, actualType: 'undefined' };
    return resolveArrayShape<T>(t(key, { returnObjects: true }), subPath);
  }, [key, t, subPath]);

  useEffect(() => {
    if (!isValid && key) {
      onInvalidRef.current(fullKey, actualType);
    }
  }, [isValid, fullKey, key, actualType]);

  return value;
};

export default useTranslatedArray;
